/**
 * The annihilator auction of the original game (design D7): "king of the hill" for the Annihilator, paid in DIAMONDS
 * instead of the original's VK votes. Rules (INTERF menu_auc_rules, FWP.as:15203-15350, net_complete_auc):
 *  - a player buys at the current price; the price then goes up by 1 and the buyer becomes the holder
 *    (net_complete_auc: price++, user_id = buyer, last_buy = now); the holder cannot buy from himself (the original
 *    hid auc_buy for him);
 *  - the holder has one Annihilator in every battle (init_game: auc_weapons); being outbid removes it, nothing is
 *    refunded;
 *  - a holder nobody outbids for config.auction.roundMs (seven days) keeps a copy for good (the original's `winners`,
 *    counted into win_auc_weapons) and a new round starts;
 *  - a battle gets (holder ? 1 : 0) + permanent copies Annihilators (BattleService annihilator source).
 * // UNVERIFIED: the original server's handling of a new round is not in the client (its 2018 snapshot kept the
 * // last winner as `user_id` at price 20). Here a new round starts without a holder at config.auction.startPrice
 * // (AUCTION_START_PRICE, default 20), which the original's rules text implies ("the bidding starts over for the next
 * // Annihilator").
 *
 * The auction is one storage document (C.auction / AUCTION_DOC_ID) changed under the AUCTION_LOCK mutex. A purchase is
 * the player.command op 'auction.buy' { expectedPrice }: the diamonds are taken from the canonical game state through
 * the rules state codec, and the new game state (with the journal entry) and the auction document are written in ONE
 * atomic storage batch (ServiceOpOutcome.commit). Lock order: the player's profile lock, then AUCTION_LOCK (nothing
 * takes a profile lock while holding AUCTION_LOCK). A round that is due ends lazily on every read and in the backend
 * sweeper (resolveDue); both broadcast auction.updated to every connected player.
 */
import { isPlainObject, type JsonObject, type JsonValue } from '../../../shared/json';
import { BackendError } from '../../../shared/protocol/errors';
import type { EventMap } from '../../../shared/protocol/messages';
import type { AuctionBuyCommandResult, AuctionHoldings, AuctionInfo, AuctionState, AuctionWinner, PlayerId } from '../../../shared/protocol/model';
import { RuleError, argsObject, decodeState, encodeState, int32, intArg } from '../../../game/rules/index';
import { ruleFailure } from '../rules-runtime';
import { type StorageBatchOp, getOrSkipDamaged, putOp, skipDamaged } from '../storage';
import type { AuthService } from './auth';
import type { CommandService, ServiceOpContext, ServiceOpOutcome } from './commands';
import { type ServiceDeps, C } from './deps';

/** Id of the auction document in C.auction. */
export const AUCTION_DOC_ID = 'annihilator';
/** Mutex key of every read-modify-write of the auction document. */
export const AUCTION_LOCK = 'auction';
/** The weapon the auction sells (WEAPONS_DEF id; the game adds it to the battle team, FWP.init_game). */
export const AUCTION_ITEM = 'annihilator';
/** Highest price a purchase may name (a sanity bound of the argument). */
const MAX_PRICE = 1_000_000_000;

/** The stored auction document. */
export interface AuctionRecord {
  itemId: string;
  /** Diamonds of the next purchase. */
  price: number;
  holderId: PlayerId | null;
  /** The holder's display name at the purchase (the view shows the current one when the account exists). */
  holderName: string | null;
  /** Epoch ms of the holder's purchase. */
  since: number | null;
  round: number;
  /** Purchases in the current round. */
  purchases: number;
  /** Every earlier round's winner, oldest first (one permanent copy per entry). */
  winners: AuctionWinner[];
  updatedAt: number;
}

function isRecord(v: unknown): v is AuctionRecord {
  return isPlainObject(v) && typeof v.price === 'number' && typeof v.round === 'number' && Array.isArray(v.winners);
}

export class AuctionService {
  constructor(
    private readonly d: ServiceDeps,
    private readonly auth: AuthService,
  ) {}

  private get cfg() {
    return this.d.config.auction;
  }

  private fresh(now: number): AuctionRecord {
    return { itemId: AUCTION_ITEM, price: Math.max(1, Math.floor(this.cfg.startPrice)), holderId: null, holderName: null, since: null, round: 1, purchases: 0, winners: [], updatedAt: now };
  }

  /** The stored document (a new auction when there is none). Does not end a due round. */
  private async read(now = this.d.clock.now()): Promise<AuctionRecord> {
    const doc = await this.d.storage.get<AuctionRecord>(C.auction, AUCTION_DOC_ID);
    return isRecord(doc) ? doc : this.fresh(now);
  }

  /** When the holder's round ends (null without a holder). */
  private endsAt(rec: AuctionRecord): number | null {
    return rec.holderId !== null && rec.since !== null ? rec.since + this.cfg.roundMs : null;
  }

  /**
   * Ends the round of `rec` in place when its holder stayed unchallenged for a whole round: the holder becomes a
   * winner (a permanent copy) and a new round starts without a holder at the start price. Returns whether it did.
   */
  private endRoundIfDue(rec: AuctionRecord, now: number): boolean {
    const end = this.endsAt(rec);
    if (end === null || now < end) return false;
    rec.winners.push({ playerId: rec.holderId!, displayName: rec.holderName ?? '', wonAt: end, price: Math.max(1, rec.price - 1) });
    this.d.logger.info('auction round won', { playerId: rec.holderId, round: rec.round, price: rec.price - 1 });
    rec.holderId = null;
    rec.holderName = null;
    rec.since = null;
    rec.round += 1;
    rec.purchases = 0;
    rec.price = Math.max(1, Math.floor(this.cfg.startPrice));
    rec.updatedAt = now;
    return true;
  }

  /**
   * The current auction: a round that is due ends first (stored, auction.updated to everybody). Takes AUCTION_LOCK
   * only when something must be written.
   */
  async current(now = this.d.clock.now()): Promise<AuctionRecord> {
    const seen = await this.read(now);
    const end = this.endsAt(seen);
    if (end === null || now < end) return seen;
    await this.resolveDue(now);
    return this.read(now);
  }

  /** Ends a due round (the backend sweeper calls it; reads do too). Returns whether a round ended. */
  async resolveDue(now = this.d.clock.now()): Promise<boolean> {
    const ended = await this.d.mutex.run(AUCTION_LOCK, async () => {
      // A damaged auction document has no round to end (logged once) until an operator restores it; the auction's own
      // requests answer UNAVAILABLE 'document_damaged', the sweeper goes on with its other work.
      const doc = await getOrSkipDamaged<AuctionRecord>(this.d.storage, C.auction, AUCTION_DOC_ID, this.d.logger);
      if (!isRecord(doc)) return null;
      const rec = doc;
      if (!this.endRoundIfDue(rec, now)) return null;
      await this.d.storage.put(C.auction, AUCTION_DOC_ID, rec);
      return rec;
    });
    if (ended) this.announce(await this.publicState(ended));
    return ended !== null;
  }

  /**
   * Operator API (admin console): ends the current round now. With a holder and `award`, the holder wins it as if the
   * round had run out (a permanent copy); without `award` the holder just loses the lot. Either way a new round starts
   * without a holder at the start price; auction.updated goes to everybody. Returns the stored document and the winner.
   */
  async endRoundByOperator(award: boolean, now = this.d.clock.now()): Promise<{ record: AuctionRecord; winner: AuctionWinner | null; endedRound: number }> {
    const out = await this.d.mutex.run(AUCTION_LOCK, async () => {
      const rec = await this.read(now);
      const endedRound = rec.round;
      let winner: AuctionWinner | null = null;
      if (rec.holderId !== null && award) {
        winner = { playerId: rec.holderId, displayName: rec.holderName ?? '', wonAt: now, price: Math.max(1, rec.price - 1) };
        rec.winners.push(winner);
      }
      rec.holderId = null;
      rec.holderName = null;
      rec.since = null;
      rec.round += 1;
      rec.purchases = 0;
      rec.price = Math.max(1, Math.floor(this.cfg.startPrice));
      rec.updatedAt = now;
      await this.d.storage.put(C.auction, AUCTION_DOC_ID, rec);
      this.d.logger.warn('auction round ended by an operator', { round: endedRound, winner: winner?.playerId ?? null });
      return { record: rec, winner, endedRound };
    });
    this.announce(await this.publicState(out.record));
    return out;
  }

  /**
   * Account deletion (./account-deletion.ts; the caller holds AUCTION_LOCK): the auction without the player. A round
   * that is due ends first (as on every read). A lot the player holds goes back to nobody at its current price in the
   * same round: the next buyer pays that price and starts the holder's week anew. The player's permanent copies (the
   * rounds it won) leave the winners list. Returns the write (none when the player has nothing in the auction) and the
   * new document, which the caller announces (announce) once it is stored.
   */
  async deletionOps(playerId: PlayerId, now = this.d.clock.now()): Promise<{ ops: StorageBatchOp[]; record: AuctionRecord | null }> {
    const stored = await this.d.storage.get<AuctionRecord>(C.auction, AUCTION_DOC_ID);
    if (!isRecord(stored)) return { ops: [], record: null };
    const rec: AuctionRecord = { ...stored, winners: [...stored.winners] };
    const due = this.endRoundIfDue(rec, now);
    const held = rec.holderId === playerId;
    const winners = rec.winners.filter((w) => w.playerId !== playerId);
    if (!due && !held && winners.length === rec.winners.length) return { ops: [], record: null };
    if (held) {
      rec.holderId = null;
      rec.holderName = null;
      rec.since = null;
    }
    rec.winners = winners;
    rec.updatedAt = now;
    return { ops: [putOp(C.auction, AUCTION_DOC_ID, rec)], record: rec };
  }

  /** Tells every connected player about a stored auction document (auction.updated). */
  async announceRecord(rec: AuctionRecord): Promise<void> {
    this.announce(await this.publicState(rec));
  }

  /** The public view of a document; the holder is shown with the current display name of the account. */
  async publicState(rec: AuctionRecord): Promise<AuctionState> {
    let holder: AuctionState['holder'] = null;
    if (rec.holderId !== null) {
      // A damaged holder account shows the name of the purchase (it must not fail the auction view of everybody).
      const a = await skipDamaged(() => this.auth.getAccount(rec.holderId!), this.d.logger, { playerId: rec.holderId });
      holder = { playerId: rec.holderId, displayName: a?.displayName ?? rec.holderName ?? '' };
    }
    const shown = Math.max(0, Math.floor(this.cfg.winnersShown));
    return {
      itemId: rec.itemId,
      price: rec.price,
      startPrice: Math.max(1, Math.floor(this.cfg.startPrice)),
      holder,
      since: rec.holderId !== null ? rec.since : null,
      endsAt: this.endsAt(rec),
      round: rec.round,
      roundMs: this.cfg.roundMs,
      purchases: rec.purchases,
      winners: shown > 0 ? rec.winners.slice(-shown).reverse() : [],
      winnerCount: rec.winners.length,
    };
  }

  /** The Annihilators `playerId` has from the auction document `rec`. */
  holdingsIn(rec: AuctionRecord, playerId: PlayerId): AuctionHoldings {
    const held = rec.holderId === playerId ? 1 : 0;
    const permanent = rec.winners.filter((w) => w.playerId === playerId).length;
    return { held, permanent, total: held + permanent };
  }

  /** auction.get: the auction and the asking player's Annihilators. */
  async info(playerId: PlayerId): Promise<AuctionInfo> {
    const now = this.d.clock.now();
    const rec = await this.current(now);
    return { ...(await this.publicState(rec)), mine: this.holdingsIn(rec, playerId), serverTime: now };
  }

  /** The annihilator source of battle.start: (holder ? 1 : 0) + permanent copies (FWP init_game). */
  async annihilatorsOf(playerId: PlayerId): Promise<number> {
    return this.holdingsIn(await this.current(), playerId).total;
  }

  private announce(state: AuctionState): void {
    this.d.hub.broadcast('auction.updated', { auction: state } satisfies EventMap['auction.updated']);
  }

  /** Makes 'auction.buy' a client-callable player.command op. */
  registerOps(commands: CommandService): void {
    commands.registerServiceOp('auction.buy', (ctx, args) => this.buy(ctx, args));
  }

  /**
   * auction.buy { expectedPrice }: the preconditions against the auction as it is now (journaled VALIDATION_FAILED:
   * 'already_holder', 'price_changed' { price }, 'not_enough_diam' { have, need }), the diamonds off the canonical
   * state, then under AUCTION_LOCK the auction is checked again (a purchase of somebody else in between: an
   * unjournaled VALIDATION_FAILED 'price_changed', nothing stored) and written in one batch with the profile.
   */
  private async buy(ctx: ServiceOpContext, args: JsonObject): Promise<ServiceOpOutcome> {
    if (this.d.config.api.disabledFeatures.includes('auction')) {
      throw new BackendError('FORBIDDEN', 'The feature "auction" is disabled on this server', { reason: 'feature_disabled', feature: 'auction' });
    }
    const o = argsObject(args, ['expectedPrice']);
    const expectedPrice = intArg(o, 'expectedPrice', { min: 1, max: MAX_PRICE });
    const seen = await this.current(ctx.nowMs);
    if (seen.holderId === ctx.playerId) throw new RuleError('already_holder', { price: seen.price });
    if (seen.price !== expectedPrice) throw new RuleError('price_changed', { price: seen.price, expectedPrice });
    const price = seen.price;
    const state = decodeState(ctx.gameState);
    if (!(price <= state.player.diam)) throw new RuleError('not_enough_diam', { have: state.player.diam, need: price });
    state.player.diam = int32(state.player.diam - price);
    ctx.setGameState(encodeState(state));

    const result: AuctionBuyCommandResult = { paid: price, auction: await this.publicState(seen), mine: this.holdingsIn(seen, ctx.playerId) };
    let bought: AuctionRecord | null = null;
    let previousHolder: PlayerId | null = null;
    return {
      result: result as unknown as JsonValue,
      commit: (write) =>
        this.d.mutex.run(AUCTION_LOCK, async () => {
          const rec = await this.read(ctx.nowMs);
          this.endRoundIfDue(rec, ctx.nowMs);
          if (rec.holderId === ctx.playerId || rec.price !== price) {
            throw ruleFailure('auction.buy', new RuleError('price_changed', { price: rec.price, expectedPrice }));
          }
          previousHolder = rec.holderId;
          rec.holderId = ctx.playerId;
          rec.holderName = ctx.account.displayName;
          rec.since = ctx.nowMs;
          rec.price = price + 1;
          rec.purchases += 1;
          rec.updatedAt = ctx.nowMs;
          result.auction = await this.publicState(rec);
          result.mine = this.holdingsIn(rec, ctx.playerId);
          await write([putOp(C.auction, AUCTION_DOC_ID, rec)]);
          bought = rec;
        }),
      afterCommit: () => {
        if (!bought) return;
        this.d.logger.info('auction purchase', { playerId: ctx.playerId, price, previousHolder, round: bought.round });
        this.announce(result.auction);
      },
    };
  }
}
